import store from '../redux/store';
import { setPreviewData } from '../redux/PreviewSlice';
import { layoutOptions } from '../layouts';

// 컴포넌트와 메뉴의 기본 인터페이스 정의
export interface Component {
  id: string;
  type: string;
  parentId?: string;
  [key: string]: any;
}

export interface Menu {
  id: string;
  name?: string;
  components?: string[];
  path?: string;
  [key: string]: any;
}

export interface PreviewData {
  components: Component[];
  menus: Menu[];
  layoutInfo: any;
  rootComponents?: Component[];
  timestamp?: string;
}

/**
 * 현재 에디터 상태를 가져와서 미리보기용 데이터를 생성합니다.
 * @returns {PreviewData} 미리보기에 필요한 데이터
 */
export const getPreviewData = (): PreviewData => {
  const state = store.getState();
  
  // 상태에서 데이터 가져오기
  const components = state.editor?.components || [];
  const menus = state.editor?.menus || [];
  const layoutInfo = state.editor?.layout || {};
  
  // 레이아웃 정보 로깅
  
  
  // 레이아웃 정보 확인
  if (!layoutInfo.selectedLayout) {
    console.warn('[미리보기 데이터 생성] 선택된 레이아웃이 없어 기본값을 사용합니다.');
  } else {
    // 선택된 레이아웃이 존재하는지 확인
    const layoutExists = layoutOptions.some(opt => opt.id === layoutInfo.selectedLayout);
    if (!layoutExists) {
      console.warn('[미리보기 데이터 생성] 존재하지 않는 레이아웃 ID:', layoutInfo.selectedLayout);
    }
  }
  
  // 유효한 컴포넌트만 필터링
  const validComponents = components.filter((comp: any) => {
    if (!comp || !comp.id || !comp.type) {
      console.warn('[미리보기 데이터 생성] 유효하지 않은 컴포넌트:', comp);
      return false;
    }
    return true;
  }) as Component[];
  
  // 루트 컴포넌트 (부모가 없는 컴포넌트) 찾기
  const rootComponents = validComponents.filter(comp => !comp.parentId);
  

  
  // 메뉴에 컴포넌트 할당 - 타입 오류 수정
  const enrichedMenus = menus.map((menu: any) => {
    // 이미 컴포넌트가 할당되어 있으면 그대로 사용
    if (menu.components && menu.components.length > 0) {
      return menu;
    }
    
    // 사용할 컴포넌트 목록
    const componentIds = rootComponents.length > 0
      ? rootComponents.map(comp => comp.id)
      : validComponents.map(comp => comp.id);
    
    
    // 없으면 루트 컴포넌트 또는 모든 컴포넌트 할당
    return {
      ...menu,
      components: componentIds
    };
  });
  
  // 최종 레이아웃 정보 객체 생성 (undefined 방지)
  const finalLayoutInfo = {
    selectedLayout: layoutInfo.selectedLayout || '',
    layoutProps: layoutInfo.layoutProps || {}
  };
  
  
  
  // 결과 데이터 생성
  const previewData: PreviewData = {
    components: validComponents,
    menus: enrichedMenus as Menu[],
    layoutInfo: finalLayoutInfo,
    rootComponents: rootComponents,
    timestamp: new Date().toISOString()
  };
  
  
  
  return previewData;
};

/**
 * 현재 브라우저 URL에서 메뉴 경로를 추출합니다.
 * @returns {string} 현재 URL에서 추출한 메뉴 경로
 */
export const getCurrentMenuPath = (): string => {
  const pathname = window.location.pathname;
  
  // URL이 /edit/ 형식인 경우 메뉴 경로 추출
  if (pathname.startsWith('/edit')) {
    // /edit 이후의 경로를 반환 (없으면 '/')
    return pathname.replace('/edit', '') || '/';
  }
  
  // 기본 경로
  return '/';
};

/**
 * 미리보기 창을 열어 현재 프로젝트를 표시합니다.
 * @param {string} menuPath - 미리보기할 메뉴 경로 (기본값: '/')
 * @returns {Window} 미리보기 창 객체
 */
export const openPreviewWindow = (menuPath = '/'): Window | null => {
  try {
    // 로컬 스토리지 초기화 (이전 데이터 제거)
    localStorage.removeItem('previewData');
    localStorage.removeItem('previewPath');
    localStorage.removeItem('previewTimestamp');
    
    // 미리보기 데이터 생성
    const previewData = getPreviewData();
    
    // 데이터 검증
    if (!previewData.components || previewData.components.length === 0) {
      alert('미리보기할 컴포넌트가 없습니다. 먼저 컴포넌트를 추가해주세요.');
      return null;
    }
    
    // Redux 스토어에 미리보기 데이터 저장
    store.dispatch(setPreviewData(previewData));
    

    
    // 로컬 스토리지에 데이터 저장
    try {
      const previewDataString = JSON.stringify(previewData);
      localStorage.setItem('previewData', previewDataString);
      localStorage.setItem('previewPath', menuPath);
      localStorage.setItem('previewTimestamp', new Date().toISOString());
      
      
    } catch (error) {
      console.error('[미리보기 창 열기] 데이터 저장 오류:', error);
      alert('미리보기 데이터를 저장하는 중 오류가 발생했습니다.');
      return null;
    }
    
    // 미리보기 URL 생성
    const previewUrl = `/preview${menuPath}`;
    
    // 미리보기 창 열기
    const previewWindow = window.open(previewUrl, 'preview', 'width=1024,height=768');
    
    if (!previewWindow) {
      alert('미리보기 창을 열 수 없습니다. 팝업 차단이 활성화되어 있지 않은지 확인하세요.');
      return null;
    }
    
    return previewWindow;
  } catch (error: any) {
    console.error('[미리보기 오류]:', error);
    alert(`미리보기 창을 여는 중 오류가 발생했습니다: ${error.message || '알 수 없는 오류'}`);
    return null;
  }
};

/**
 * 현재 작업 중인 메뉴에 대한 미리보기를 엽니다.
 * @returns {Window} 미리보기 창 객체
 */
export const previewCurrentMenu = (): Window | null => {
  // 현재 URL에서 메뉴 경로 추출
  const currentMenuPath = getCurrentMenuPath();
  
  // 현재 경로로 미리보기 열기
  return openPreviewWindow(currentMenuPath);
}; 

/**
 * 현재 작업 중인 메뉴에 대한 미리보기를 엽니다.
 * @returns {Window} 미리보기 창 객체
 */
export const previewCurrentMenuWithoutNewWindow = (): Window | null => {
  // 현재 URL에서 메뉴 경로 추출
  const currentMenuPath = getCurrentMenuPath();
  
  // 현재 경로로 미리보기 열기
  return null;
}; 